import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, authUtils } from '../api/userAPI';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      console.log('Checking authentication on app load...');
      const token = authUtils.getToken();
      console.log('Token from localStorage:', token);
      if (token) {
        try {
          console.log('Attempting to get profile with stored token...');
          const userData = await authAPI.getProfile(token);
          console.log('Profile data from stored token:', userData);
          setUser(userData);
        } catch (err) {
          console.log('Token validation failed, removing token:', err.message);
          // Token is invalid, remove it
          authUtils.logout();
        }
      } else {
        console.log('No token found in localStorage');
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Starting login process with:', credentials.email);
      const response = await authAPI.login(credentials);
      console.log('Login response:', response);
      
      if (!response.access_token) {
        throw new Error('No access token received from server');
      }
      
      // Store token
      authUtils.setToken(response.access_token);
      console.log('Token stored:', response.access_token);
      
      // Fetch user profile with the token
      console.log('Fetching user profile...');
      const userData = await authAPI.getProfile(response.access_token);
      console.log('User data fetched:', userData);
      
      if (!userData) {
        throw new Error('No user data received from server');
      }
      
      if (!userData.role) {
        console.warn('User data missing role field:', userData);
      }
      
      authUtils.setUser(userData);
      setUser(userData);
      console.log('User state updated, role:', userData.role);
      
      return response;
    } catch (err) {
      console.error('Login error details:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authAPI.register(userData);
      
      // After successful registration, automatically log in
      if (response.success) {
        await login({
          email: userData.email,
          password: userData.password
        });
      }
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authUtils.logout();
    setUser(null);
    setError(null);
  };

  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = authUtils.getToken();
      const response = await authAPI.updateProfile(profileData, token);
      
      // Update local user data
      const updatedUser = { ...user, ...profileData };
      setUser(updatedUser);
      authUtils.setUser(updatedUser);
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async (token) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authAPI.verifyEmail(token);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authAPI.forgotPassword(email);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (resetData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authAPI.resetPassword(resetData);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const token = authUtils.getToken();
      if (token) {
        const userData = await authAPI.getProfile(token);
        setUser(userData);
        authUtils.setUser(userData);
        return userData;
      }
    } catch (err) {
      console.error('Failed to refresh user data:', err);
      // Don't throw error here as it might break the UI
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateProfile,
    verifyEmail,
    forgotPassword,
    resetPassword,
    refreshUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
